import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  AlertTriangle, UserX, BookX, CheckCircle, Clock, 
  RefreshCw, Search, GraduationCap, ArrowRight, 
  FileText, Download, Filter, Edit, Award
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FailedStudents = () => {
  const [loading, setLoading] = useState(true);
  const [failedStudents, setFailedStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [allSemesters, setAllSemesters] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    course_id: '',
    semester_id: '',
    exam_id: '',
    status: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    cleared: 0,
    promoted: 0
  });
  
  // Update Dialog
  const [updateDialog, setUpdateDialog] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [subjectUpdates, setSubjectUpdates] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchDropdowns();
  }, []);

  useEffect(() => {
    if (filters.exam_id) {
      fetchFailedStudents();
    }
  }, [filters.exam_id, filters.status]);

  const fetchDropdowns = async () => {
    try {
      const [coursesRes, semestersRes, examsRes, subjectsRes] = await Promise.all([
        axios.get(`${API_URL}/api/master-settings/courses`, { headers }),
        axios.get(`${API_URL}/api/master-settings/semesters`, { headers }),
        axios.get(`${API_URL}/api/examination/exams`, { headers }),
        axios.get(`${API_URL}/api/master-settings/subjects`, { headers })
      ]);
      
      setCourses(coursesRes.data || []);
      setAllSemesters(semestersRes.data || []);
      setSemesters(semestersRes.data || []);
      setExams(examsRes.data || []);
      setSubjects(subjectsRes.data || []);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchFailedStudents = async () => {
    if (!filters.exam_id) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('exam_id', filters.exam_id);
      if (filters.course_id) params.append('course_id', filters.course_id);
      if (filters.semester_id) params.append('semester_id', filters.semester_id);
      if (filters.status) params.append('status', filters.status);
      
      const response = await axios.get(
        `${API_URL}/api/examination/failed-students?${params.toString()}`,
        { headers }
      );
      
      setFailedStudents(response.data.students || []);
      setStats(response.data.stats || { total: 0, pending: 0, cleared: 0, promoted: 0 });
    } catch (error) {
      console.error('Error fetching failed students:', error);
      toast.error('Failed to load failed students');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseChange = (courseId) => {
    setFilters({ ...filters, course_id: courseId, semester_id: '' });
    if (courseId) {
      const filtered = allSemesters.filter(s => s.course_id === courseId);
      setSemesters(filtered);
    } else {
      setSemesters(allSemesters);
    }
  };

  const openUpdateDialog = (student) => {
    setSelectedStudent(student);
    // Initialize subject updates with current status
    const updates = {};
    student.failed_subjects?.forEach(subj => {
      updates[subj.subject_id] = {
        reexam_marks: subj.reexam_marks || '',
        status: subj.status || 'failed',
        subject_name: subj.subject_name,
        original_marks: subj.marks_obtained,
        total_marks: subj.total_marks
      };
    });
    setSubjectUpdates(updates);
    setUpdateDialog(true);
  };

  const handleSubjectUpdate = (subjectId, field, value) => {
    setSubjectUpdates(prev => ({
      ...prev,
      [subjectId]: {
        ...prev[subjectId],
        [field]: value
      }
    }));
  };

  const saveSubjectUpdates = async () => {
    if (!selectedStudent) return;
    
    try {
      // Prepare update data
      const updates = Object.entries(subjectUpdates).map(([subjectId, data]) => ({
        subject_id: subjectId,
        reexam_marks: data.reexam_marks ? parseFloat(data.reexam_marks) : null,
        status: data.status
      }));
      
      await axios.put(
        `${API_URL}/api/examination/failed-students/${selectedStudent.id}/update-subjects`,
        { 
          exam_id: filters.exam_id,
          subject_updates: updates 
        },
        { headers }
      );
      
      toast.success('Subject statuses updated successfully');
      setUpdateDialog(false);
      setSelectedStudent(null);
      fetchFailedStudents();
    } catch (error) {
      console.error('Error updating subjects:', error);
      toast.error(error.response?.data?.detail || 'Failed to update');
    }
  };

  const handlePromote = async (studentId) => {
    if (!window.confirm('Are you sure you want to promote this student? All failed subjects must be cleared first.')) {
      return;
    }
    
    try {
      await axios.post(
        `${API_URL}/api/examination/failed-students/${studentId}/promote`,
        { exam_id: filters.exam_id },
        { headers }
      );
      
      toast.success('Student promoted successfully!');
      fetchFailedStudents();
    } catch (error) {
      console.error('Error promoting student:', error);
      toast.error(error.response?.data?.detail || 'Cannot promote - clear all failed subjects first');
    }
  };

  const exportToExcel = () => {
    if (failedStudents.length === 0) {
      toast.error('No data to export');
      return;
    }

    const exportData = failedStudents.map((s, index) => ({
      'SL': index + 1,
      'Student Name': s.student_name,
      'Admission No': s.admission_number,
      'Roll No': s.roll_number,
      'Course': s.course_name,
      'Semester': s.semester_name,
      'Failed Subjects': s.failed_subjects?.map(sub => sub.subject_name).join(', '),
      'Failed Count': s.failed_subject_count,
      'Status': s.status
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Failed Students');
    XLSX.writeFile(wb, `Failed_Students_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel file downloaded!');
  };

  const filteredStudents = failedStudents.filter(s => {
    if (searchTerm && !s.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !s.admission_number?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    return true;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700"><Clock className="h-3 w-3 mr-1" /> Pending</Badge>;
      case 'cleared':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" /> Cleared</Badge>;
      case 'promoted':
        return <Badge className="bg-blue-100 text-blue-700"><GraduationCap className="h-3 w-3 mr-1" /> Promoted</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">{status}</Badge>;
    }
  };

  const canPromote = (student) => {
    // All failed subjects must be cleared
    return student.failed_subjects?.every(s => s.status === 'cleared');
  };

  return (
    <div className="space-y-6 p-4 sm:p-6" data-testid="failed-students-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <UserX className="text-red-500" size={28} />
            Failed Students Management
          </h1>
          <p className="text-gray-600 mt-1">Track failed subjects, update re-exam results & promote students</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={fetchFailedStudents} variant="outline" size="sm" data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={exportToExcel} variant="outline" size="sm" data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Course</label>
              <Select value={filters.course_id || "all"} onValueChange={(v) => handleCourseChange(v === "all" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="All Courses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Courses</SelectItem>
                  {courses.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Semester</label>
              <Select value={filters.semester_id || "all"} onValueChange={(v) => setFilters({...filters, semester_id: v === "all" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="All Semesters" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Semesters</SelectItem>
                  {semesters.map(s => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Exam *</label>
              <Select value={filters.exam_id} onValueChange={(v) => setFilters({...filters, exam_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select Exam" /></SelectTrigger>
                <SelectContent>
                  {exams.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Status</label>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters({...filters, status: v === "all" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="All Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="cleared">Cleared</SelectItem>
                  <SelectItem value="promoted">Promoted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Name or Adm No..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Total Failed</p>
                <p className="text-2xl font-bold text-red-700">{stats.total}</p>
              </div>
              <UserX className="h-8 w-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Pending</p>
                <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Cleared</p>
                <p className="text-2xl font-bold text-green-700">{stats.cleared}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Promoted</p>
                <p className="text-2xl font-bold text-blue-700">{stats.promoted}</p>
              </div>
              <GraduationCap className="h-8 w-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Students List */}
      {!filters.exam_id ? (
        <Card className="p-8 text-center">
          <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">Select an Exam</h3>
          <p className="text-gray-500 mt-2">Please select an exam to view failed students</p>
        </Card>
      ) : loading ? (
        <Card className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </Card>
      ) : filteredStudents.length === 0 ? (
        <Card className="p-8 text-center">
          <Award className="h-12 w-12 mx-auto text-green-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-700">No Failed Students</h3>
          <p className="text-gray-500 mt-2">All students passed in this exam! 🎉</p>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Course/Semester</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Failed Subjects</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredStudents.map((student) => (
                    <tr key={student.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{student.student_name}</p>
                          <p className="text-sm text-gray-500">{student.admission_number}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-gray-700">{student.course_name}</p>
                        <p className="text-xs text-gray-500">{student.semester_name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {student.failed_subjects?.map((subj, idx) => (
                            <Badge 
                              key={idx} 
                              variant="outline" 
                              className={subj.status === 'cleared' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}
                            >
                              {subj.subject_name} ({subj.marks_obtained}/{subj.total_marks})
                              {subj.status === 'cleared' && ' ✓'}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(student.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-center gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openUpdateDialog(student)}
                            disabled={student.status === 'promoted'}
                          >
                            <Edit className="h-4 w-4 mr-1" /> Update
                          </Button>
                          {canPromote(student) && student.status !== 'promoted' && (
                            <Button 
                              size="sm" 
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => handlePromote(student.id)}
                            >
                              <ArrowRight className="h-4 w-4 mr-1" /> Promote
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Update Subject Dialog */}
      <Dialog open={updateDialog} onOpenChange={setUpdateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Update Failed Subjects - {selectedStudent?.student_name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4 max-h-[60vh] overflow-y-auto">
            {Object.entries(subjectUpdates).map(([subjectId, data]) => (
              <div key={subjectId} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium text-gray-900">{data.subject_name}</p>
                    <p className="text-sm text-gray-500">
                      Original: {data.original_marks}/{data.total_marks} 
                      ({((data.original_marks/data.total_marks)*100).toFixed(1)}%)
                    </p>
                  </div>
                  <Badge className={data.status === 'cleared' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {data.status}
                  </Badge>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-gray-600">Re-exam Marks</label>
                    <Input
                      type="number"
                      value={data.reexam_marks}
                      onChange={(e) => handleSubjectUpdate(subjectId, 'reexam_marks', e.target.value)}
                      placeholder="Enter marks"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-600">Status</label>
                    <Select 
                      value={data.status} 
                      onValueChange={(v) => handleSubjectUpdate(subjectId, 'status', v)}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="cleared">Cleared</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            ))}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setUpdateDialog(false)}>Cancel</Button>
            <Button onClick={saveSubjectUpdates} className="bg-blue-600 hover:bg-blue-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FailedStudents;
